import React, { useEffect, useState } from 'react';

const WORKER_SRC = `
function blur(gray, w, h) {
  const out = new Float32Array(w * h);
  for (let y = 1; y < h - 1; y++)
    for (let x = 1; x < w - 1; x++)
      out[y*w+x] = (gray[(y-1)*w+x-1]+gray[(y-1)*w+x]+gray[(y-1)*w+x+1]+gray[y*w+x-1]+gray[y*w+x]+gray[y*w+x+1]+gray[(y+1)*w+x-1]+gray[(y+1)*w+x]+gray[(y+1)*w+x+1])/9;
  return out;
}

function simplify(pts, eps) {
  const n = pts.length;
  if (n <= 2) return pts;
  const keep = new Uint8Array(n);
  keep[0] = keep[n - 1] = 1;
  const stack = [[0, n - 1]];
  const epsSq = eps * eps;
  while (stack.length) {
    const [a, b] = stack.pop();
    if (b - a < 2) continue;
    const [x1, y1] = pts[a], [x2, y2] = pts[b];
    const dx = x2 - x1, dy = y2 - y1, lenSq = dx*dx + dy*dy || 1;
    let maxD = 0, maxI = a;
    for (let i = a + 1; i < b; i++) {
      const [px, py] = pts[i];
      const t = Math.max(0, Math.min(1, ((px-x1)*dx + (py-y1)*dy) / lenSq));
      const d = (x1+t*dx-px)**2 + (y1+t*dy-py)**2;
      if (d > maxD) { maxD = d; maxI = i; }
    }
    if (maxD > epsSq) {
      keep[maxI] = 1;
      stack.push([a, maxI], [maxI, b]);
    }
  }
  return pts.filter((_, i) => keep[i]);
}

self.onmessage = function({ data: msg }) {
  const { buf, w, h, origW, origH, edgeThreshold, simplifyEps, minPathLength, maxGap } = msg;
  const pixels = new Uint8ClampedArray(buf);

  let gray = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++)
    gray[i] = 0.299*pixels[i*4] + 0.587*pixels[i*4+1] + 0.114*pixels[i*4+2];

  let lo = 255, hi = 0;
  for (let i = 0; i < gray.length; i++) { if (gray[i] < lo) lo = gray[i]; if (gray[i] > hi) hi = gray[i]; }
  const rng = hi - lo || 1;
  for (let i = 0; i < gray.length; i++) gray[i] = (gray[i] - lo) / rng * 255;

  gray = blur(blur(blur(gray, w, h), w, h), w, h);

  const mag = new Float32Array(w * h);
  const angArr = new Float32Array(w * h);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const gx = -gray[(y-1)*w+x-1]+gray[(y-1)*w+x+1]-2*gray[y*w+x-1]+2*gray[y*w+x+1]-gray[(y+1)*w+x-1]+gray[(y+1)*w+x+1];
      const gy = -gray[(y-1)*w+x-1]-2*gray[(y-1)*w+x]-gray[(y-1)*w+x+1]+gray[(y+1)*w+x-1]+2*gray[(y+1)*w+x]+gray[(y+1)*w+x+1];
      mag[y*w+x] = Math.sqrt(gx*gx + gy*gy);
      angArr[y*w+x] = Math.atan2(gy, gx);
    }
  }

  const edges = new Uint8Array(w * h);
  const nms4 = [[1,0],[1,1],[0,1],[-1,1]];
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const m = mag[y*w+x];
      if (m < edgeThreshold) continue;
      const [dx, dy] = nms4[(((Math.round(angArr[y*w+x] / (Math.PI/4)) % 4) + 4) % 4)];
      if (m >= mag[(y+dy)*w+x+dx] && m >= mag[(y-dy)*w+x-dx]) edges[y*w+x] = 1;
    }
  }

  const visited = new Uint8Array(w * h);
  const dirs8 = [[1,0],[0,1],[-1,0],[0,-1],[1,1],[-1,1],[1,-1],[-1,-1]];
  const allPaths = [];

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!edges[y*w+x] || visited[y*w+x]) continue;
      const pts = [];
      let cx = x, cy = y, pdx = 0, pdy = 0;
      while (true) {
        visited[cy*w+cx] = 1;
        pts.push([cx, cy]);
        let nx = -1, ny = -1, best = -Infinity;
        for (const [dx, dy] of dirs8) {
          const qx = cx+dx, qy = cy+dy;
          if (qx < 0 || qx >= w || qy < 0 || qy >= h) continue;
          if (!edges[qy*w+qx] || visited[qy*w+qx]) continue;
          const score = pdx*dx + pdy*dy;
          if (score > best) { best = score; nx = qx; ny = qy; }
        }
        if (nx === -1) break;
        pdx = nx - cx; pdy = ny - cy;
        cx = nx; cy = ny;
      }
      if (pts.length >= minPathLength) allPaths.push(pts);
    }
  }

  // Spatial grid bridge finding — O(n) instead of O(n²)
  const cellSize = maxGap;
  const gridW = Math.ceil(w / cellSize) + 2;
  const grid = new Map();
  const endpoints = [];
  for (let pi = 0; pi < allPaths.length; pi++) {
    const p = allPaths[pi];
    endpoints.push({ pi, x: p[0][0], y: p[0][1] });
    endpoints.push({ pi, x: p[p.length-1][0], y: p[p.length-1][1] });
  }
  for (let i = 0; i < endpoints.length; i++) {
    const { x, y } = endpoints[i];
    const key = (y / cellSize | 0) * gridW + (x / cellSize | 0);
    if (!grid.has(key)) grid.set(key, []);
    grid.get(key).push(i);
  }

  const bridges = [];
  const usedEp = new Set();
  const maxGapSq = maxGap * maxGap;
  for (let i = 0; i < endpoints.length; i++) {
    if (usedEp.has(i)) continue;
    const a = endpoints[i];
    const gx0 = a.x / cellSize | 0, gy0 = a.y / cellSize | 0;
    let bestD = maxGapSq, bestJ = -1;
    for (let gy = gy0 - 1; gy <= gy0 + 1; gy++) {
      for (let gx = gx0 - 1; gx <= gx0 + 1; gx++) {
        const bucket = grid.get(gy * gridW + gx);
        if (!bucket) continue;
        for (const j of bucket) {
          if (j <= i || usedEp.has(j)) continue;
          const b = endpoints[j];
          if (b.pi === a.pi) continue;
          const d2 = (b.x-a.x)**2 + (b.y-a.y)**2;
          if (d2 < bestD) { bestD = d2; bestJ = j; }
        }
      }
    }
    if (bestJ !== -1) {
      const b = endpoints[bestJ];
      bridges.push([[a.x, a.y], [b.x, b.y]]);
      usedEp.add(i); usedEp.add(bestJ);
    }
  }

  const inv = origW / w;
  const parts = [];
  for (const pts of allPaths) {
    const s = simplify(pts, simplifyEps);
    if (s.length < 2) continue;
    let seg = 'M'+(s[0][0]*inv|0)+' '+(s[0][1]*inv|0);
    for (let i = 1; i < s.length; i++) seg += 'L'+(s[i][0]*inv|0)+' '+(s[i][1]*inv|0);
    parts.push(seg);
  }
  for (const [[ax,ay],[bx,by]] of bridges)
    parts.push('M'+(ax*inv|0)+' '+(ay*inv|0)+'L'+(bx*inv|0)+' '+(by*inv|0));

  self.postMessage({ path: parts.join(''), origW, origH });
};
`;

function ContourPortrait({
  src,
  processSize = 300,
  edgeThreshold = 14,
  simplifyEps = 7,
  minPathLength = 20,
  maxGap = 12,
  stroke = 'rgba(255,255,255,0.85)',
}) {
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!src) return;
    const blob = new Blob([WORKER_SRC], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const worker = new Worker(url);

    worker.onmessage = ({ data }) => {
      setResult(data);
      worker.terminate();
      URL.revokeObjectURL(url);
    };

    const img = new Image();
    img.onload = () => {
      const origW = img.naturalWidth, origH = img.naturalHeight;
      const scale = Math.min(1, processSize / Math.max(origW, origH));
      const w = Math.round(origW * scale), h = Math.round(origH * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      const { data } = ctx.getImageData(0, 0, w, h);
      worker.postMessage({ buf: data.buffer, w, h, origW, origH, edgeThreshold, simplifyEps, minPathLength, maxGap }, [data.buffer]);
    };
    img.src = src;

    return () => { worker.terminate(); URL.revokeObjectURL(url); };
  }, [src, processSize, edgeThreshold, simplifyEps, minPathLength, maxGap]);

  if (!result) return null;

  return (
    <svg viewBox={`0 0 ${result.origW} ${result.origH}`} style={{ width: '100%', display: 'block' }} xmlns="http://www.w3.org/2000/svg">
      <path d={result.path} stroke={stroke} strokeWidth="1" strokeLinecap="round" fill="none" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export default ContourPortrait;
